'use client';
/**
 * WireFilterChips — the ONE thin client island on the wire.
 *
 * Mirrors `ReadFilterChips` / the Newsroom `NewsroomFilter` mechanism: the page
 * stays a server-rendered RSC — every row + brief ships complete in the crawlable
 * HTML, tagged with `data-wire-type="<type>"`. This island renders only the sticky
 * mono-uppercase chip row and toggles VISIBILITY by stamping `data-wire-filter`
 * on the `#wire-grid` container; a tiny inline CSS rule hides non-matching items.
 * A second imperative pass collapses day sections that end up empty and recomputes
 * the band counts, so filtered days don't leave a dangling band.
 *
 * No server `searchParams` read and no router round-trip → the route keeps its
 * ISR; a deep-linked `?type=breaking` is adopted on mount and mirrored back via
 * `history.replaceState`. The `recaps` chip matches BOTH `daily` and `weekly`.
 *
 * Sticky offset is `top-[56px]` (under the 56px TopBar); day bands carry
 * `scroll-margin-top:118px` so a permalink jump clears both the TopBar and this
 * sticky chip row (the flagged port note).
 */
import { useEffect, useState } from 'react';
import { READ_TYPE_LABEL, type ReadItemType } from '@/lib/read/types';

type FilterKey =
  | 'all'
  | 'breaking'
  | 'news'
  | 'analysis'
  | 'guide'
  | 'recaps'
  | 'edge';

const CHIPS: { key: FilterKey; label: string; types: ReadItemType[] }[] = [
  { key: 'all', label: 'All', types: [] },
  { key: 'breaking', label: READ_TYPE_LABEL.breaking, types: ['breaking'] },
  { key: 'news', label: READ_TYPE_LABEL.news, types: ['news'] },
  { key: 'analysis', label: READ_TYPE_LABEL.analysis, types: ['analysis'] },
  { key: 'guide', label: 'Guides', types: ['guide'] },
  { key: 'recaps', label: 'Recaps', types: ['daily', 'weekly'] },
  { key: 'edge', label: 'Edges', types: ['edge'] },
];

const isFilterKey = (v: string): v is FilterKey => CHIPS.some((c) => c.key === v);

export const WIRE_GRID_ID = 'wire-grid';

export interface WireFilterChipsProps {
  /** Per-type counts across the wire (from `WireData.counts`). */
  counts: Record<ReadItemType, number>;
}

export default function WireFilterChips({ counts }: WireFilterChipsProps) {
  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('type');
    if (param && isFilterKey(param)) {
      setFilter(param);
      applyFilter(param);
    }
  }, []);

  const total = Object.values(counts).reduce((s, n) => s + n, 0);
  const countFor = (chip: (typeof CHIPS)[number]): number =>
    chip.key === 'all'
      ? total
      : chip.types.reduce((s, t) => s + (counts[t] ?? 0), 0);

  const select = (key: FilterKey) => {
    setFilter(key);
    applyFilter(key);
    const url = new URL(window.location.href);
    if (key === 'all') url.searchParams.delete('type');
    else url.searchParams.set('type', key);
    window.history.replaceState(null, '', url.toString());
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <div
        role="group"
        aria-label="Filter the wire"
        className="sticky top-[56px] z-40 mb-2 flex flex-wrap gap-1.5 border-b border-divider bg-ink/85 py-2.5 backdrop-blur-md"
      >
        {CHIPS.map((chip) => {
          const active = filter === chip.key;
          const n = countFor(chip);
          if (chip.key !== 'all' && n === 0) return null;
          return (
            <button
              key={chip.key}
              type="button"
              aria-pressed={active}
              onClick={() => select(chip.key)}
              className={`inline-flex items-center gap-1.5 rounded border px-3 py-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                active
                  ? 'border-scout-gold/40 bg-scout-gold-dim text-scout-gold'
                  : 'border-divider bg-surface text-parchment-dim hover:border-scout-gold/30 hover:text-parchment'
              }`}
            >
              {chip.label}
              <span className={active ? 'text-scout-gold/70' : 'text-parchment-dim/70'}>{n}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

/** Stamp the active filter onto the grid, then collapse empty days + recount bands. */
function applyFilter(key: FilterKey) {
  if (typeof document === 'undefined') return;
  const grid = document.getElementById(WIRE_GRID_ID);
  if (!grid) return;
  if (key === 'all') grid.removeAttribute('data-wire-filter');
  else grid.setAttribute('data-wire-filter', key);

  // CSS hides non-matching [data-wire-type]; now collapse days with zero visible
  // items and recompute each band's "· N items" count.
  const chip = CHIPS.find((c) => c.key === key);
  const allowed = key === 'all' ? null : new Set(chip?.types ?? []);
  grid.querySelectorAll<HTMLElement>('[data-wire-day]').forEach((day) => {
    const items = day.querySelectorAll<HTMLElement>('[data-wire-type]');
    let visible = 0;
    items.forEach((it) => {
      const type = it.getAttribute('data-wire-type') as ReadItemType | null;
      if (!allowed || (type && allowed.has(type))) visible += 1;
    });
    day.style.display = visible === 0 ? 'none' : '';
    const ct = day.querySelector<HTMLElement>('[data-band-count]');
    if (ct) ct.textContent = `· ${visible} ${visible === 1 ? 'item' : 'items'}`;
  });
}

// When the grid carries data-wire-filter="<key>", hide every item whose
// data-wire-type isn't in that key's set. 'recaps' keeps daily + weekly. Reveal
// with `display:revert` (NOT a forced flex) so each element keeps its natural box
// — <a> rows stay flex, <details> breaking rows stay block, the <article> brief
// stays flex — none get clobbered.
const G = `#${WIRE_GRID_ID}`;
const CSS = `
${G}[data-wire-filter] [data-wire-type] { display: none; }
${G}[data-wire-filter="breaking"] [data-wire-type="breaking"],
${G}[data-wire-filter="news"] [data-wire-type="news"],
${G}[data-wire-filter="analysis"] [data-wire-type="analysis"],
${G}[data-wire-filter="guide"] [data-wire-type="guide"],
${G}[data-wire-filter="edge"] [data-wire-type="edge"],
${G}[data-wire-filter="recaps"] [data-wire-type="daily"],
${G}[data-wire-filter="recaps"] [data-wire-type="weekly"] { display: revert; }
${G}[data-wire-filter] [data-wire-since] { display: none; }
`;
